import { User } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ClubBadge from "../../ClubBadge";
import { getClubBadge } from "../../../data/clubBadges";
import ConfirmModal from "../../ConfirmModal";
import Modal from "../../Modal";
import ClubMultiSelect from "../shared/ClubMultiSelect";
import {
  linkClubsToCompetition, unlinkClubFromCompetition,
  AdminCompetition, CompetitionClub,
} from "../../../api/admin";
import { Club } from "../../../api/clubs";
import {
  card, primaryBtn, secondaryBtn, smallBtn,
  panelHeading, badge, errorStyle,
} from "../../../styles";

interface Props {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
  allClubs: Club[];
}

export default function ClubsTab({ user, comp, reload, allClubs }: Props) {
  const { t } = useTranslation();
  const [addClubModal, setAddClubModal] = useState(false);
  const [selectedClubIds, setSelectedClubIds] = useState<string[]>([]);
  const [linkingClub, setLinkingClub] = useState(false);
  const [confirmUnlinkClub, setConfirmUnlinkClub] = useState<CompetitionClub | null>(null);
  const [unlinkingClub, setUnlinkingClub] = useState(false);
  const [error, setError] = useState("");

  const handleLinkClub = async () => {
    if (selectedClubIds.length === 0) return;
    setLinkingClub(true);
    try {
      await linkClubsToCompetition(user, comp.id, selectedClubIds);
      setAddClubModal(false); setSelectedClubIds([]);
      await reload();
    } catch {
      setError(t("admin.competitions.entrants.errors.failedToAdd"));
    } finally {
      setLinkingClub(false);
    }
  };

  const handleUnlinkClub = async () => {
    if (!confirmUnlinkClub) return;
    setUnlinkingClub(true);
    try {
      await unlinkClubFromCompetition(user, comp.id, confirmUnlinkClub.id);
      setConfirmUnlinkClub(null);
      await reload();
    } catch {
      setError(t("admin.competitions.entrants.errors.failedToRemove"));
    } finally {
      setUnlinkingClub(false);
    }
  };

  return (
    <>
      <div style={card}>
        {error && <p style={{ ...errorStyle, marginBottom: "0.75rem" }}>{error}</p>}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
          <h2 style={{ ...panelHeading, margin: 0 }}>
            {t("admin.competitions.entrants.title")}
            <span style={{ marginLeft: "0.5rem", fontWeight: 400, color: "var(--color-text-muted)" }}>
              ({comp.clubs.length})
            </span>
          </h2>
          <button style={smallBtn} onClick={() => { setSelectedClubIds([]); setAddClubModal(true); }}>{t("admin.competitions.entrants.add")}</button>
        </div>
        {comp.clubs.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.competitions.entrants.noEntrants")}</p>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {comp.clubs.map((club) => (
                <span key={club.id} style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text)", display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.4rem 0.65rem" }}>
                  <ClubBadge config={getClubBadge(club.name)} size={16} title={club.name} />
                  <span style={{ fontWeight: 500 }}>{club.name}</span>
                  {club.shortName && club.shortName !== club.name && (
                    <span style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>({club.shortName})</span>
                  )}
                  {club.externalTeamId && (
                    <span style={{ color: "var(--color-text-dim)", fontSize: "0.72rem" }} title={t("admin.competitions.entrants.externalIdTitle")}>
                      #{club.externalTeamId}
                    </span>
                  )}
                  <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: "0 0.1rem", fontSize: "0.8rem", lineHeight: 1 }}
                    onClick={() => setConfirmUnlinkClub(club)} title={t("admin.competitions.entrants.removeTitle")}>✕</button>
                </span>
              ))}
            </div>
            <Link to={`/admin/competitions/${comp.id}/data-source`} style={{ display: "inline-block", marginTop: "0.75rem", fontSize: "0.78rem", color: "var(--color-accent)", textDecoration: "none" }}>
              {t("admin.competitions.entrants.mapExternalIds")}
            </Link>
          </>
        )}
      </div>

      {/* Add team modal */}
      {addClubModal && (() => {
        const unlinkedClubs = allClubs.filter((c) => !comp.clubs.find((lc) => lc.id === c.id));
        return (
          <Modal onClose={() => { setAddClubModal(false); setSelectedClubIds([]); }} blur={false}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "480px", width: "90%", display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
              <h3 style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                {t("admin.competitions.entrants.addModal.title", { name: comp.name })}
                {selectedClubIds.length > 0 && (
                  <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem", fontWeight: 400, color: "var(--color-accent)" }}>
                    {t("admin.competitions.entrants.addModal.selectedCount", { count: selectedClubIds.length })}
                  </span>
                )}
              </h3>
              <ClubMultiSelect
                clubs={unlinkedClubs}
                selectedClubIds={selectedClubIds}
                onChange={setSelectedClubIds}
                searchPlaceholder={t("admin.competitions.entrants.addModal.searchPlaceholder")}
                emptyMessage={t("admin.competitions.entrants.addModal.allAdded")}
                noResultsMessage={t("admin.competitions.entrants.addModal.noResults")}
                clearSelectionLabel={t("admin.competitions.entrants.addModal.clearSelection")}
                maxHeight="280px"
              />
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.75rem", flexShrink: 0 }}>
                <button style={secondaryBtn} onClick={() => { setAddClubModal(false); setSelectedClubIds([]); }}>{t("common.cancel")}</button>
                <button style={primaryBtn} onClick={handleLinkClub} disabled={selectedClubIds.length === 0 || linkingClub}>
                  {linkingClub ? t("admin.competitions.entrants.addModal.adding") : `${t("common.add")} ${selectedClubIds.length > 0 ? selectedClubIds.length : ""} Team${selectedClubIds.length !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Unlink club confirm */}
      {confirmUnlinkClub && (
        <ConfirmModal
          title={t("admin.competitions.entrants.removeConfirm.title")}
          message={t("admin.competitions.entrants.removeConfirm.message", { name: confirmUnlinkClub.name, competition: comp.name })}
          confirmLabel={t("common.remove")}
          confirming={unlinkingClub}
          onConfirm={handleUnlinkClub}
          onCancel={() => setConfirmUnlinkClub(null)}
        />
      )}
    </>
  );
}
